export const sortByOrdinamento = (a, b, idKey = 'id') => {
    const oa = Number(a?.ordinamento) || 0;
    const ob = Number(b?.ordinamento) || 0;
    if (oa !== ob) return oa - ob;
    return (Number(a[idKey]) || 0) - (Number(b[idKey]) || 0);
};

export const sortContenutiByOrdinamento = (a, b) => sortByOrdinamento(a, b, 'id_contenuto');

/** Ordine misto contenuti + sotto-cartelle nello stesso parent (admin e corsista). */
export const sortMixedSiblingsByOrdinamento = (a, b) => {
    const oa = Number(a?.ordinamento) || 0;
    const ob = Number(b?.ordinamento) || 0;
    if (oa !== ob) return oa - ob;
    if (a.kind !== b.kind) {
        return a.kind === 'cont' ? -1 : 1;
    }
    const ida = a.kind === 'cont' ? a.data?.id_contenuto : a.data?.id ?? a.data?.id_area;
    const idb = b.kind === 'cont' ? b.data?.id_contenuto : b.data?.id ?? b.data?.id_area;
    return (Number(ida) || 0) - (Number(idb) || 0);
};

export const buildMixedAreaChildItems = (area) => {
    const items = [
        ...(area.contenuti || []).map((c) => ({
            kind: 'cont',
            ordinamento: c.ordinamento,
            data: c,
        })),
        ...(area.sottoaree || []).map((s) => ({
            kind: 'area',
            ordinamento: s.ordinamento,
            data: s,
        })),
    ];
    return items.sort(sortMixedSiblingsByOrdinamento);
};

/**
 * Ordine cartelle/sotto-cartelle:
 * 1) aree.ordinamento (impostato dal drag&drop admin)
 * 2) min(contenuti.ordinamento) nel sotto-albero — replica l'ordine storico
 *    della vista corsista quando le aree non avevano ancora ordinamento proprio
 * 3) id_area come ultimo tie-break
 */
const enrichAreaSortMeta = (area) => {
    let minOrd = Infinity;

    area.contenuti.forEach((c) => {
        const o = Number(c?.ordinamento);
        if (Number.isFinite(o) && o < minOrd) {
            minOrd = o;
        }
    });

    area.sottoaree.forEach((sub) => {
        enrichAreaSortMeta(sub);
        if (sub._sortMinOrd < minOrd) {
            minOrd = sub._sortMinOrd;
        }
    });

    area._sortMinOrd = minOrd === Infinity ? Number(area.id) || 0 : minOrd;
};

export const sortAreasByOrdinamento = (a, b) => {
    const oa = Number(a?.ordinamento) || 0;
    const ob = Number(b?.ordinamento) || 0;
    if (oa !== ob) return oa - ob;

    const ma = a._sortMinOrd ?? (Number(a.id) || 0);
    const mb = b._sortMinOrd ?? (Number(b.id) || 0);
    if (ma !== mb) return ma - mb;

    return (Number(a.id) || 0) - (Number(b.id) || 0);
};

const createAreaNode = ({ id, titolo, id_area_padre, ordinamento }) => ({
    id,
    titolo,
    id_area_padre: id_area_padre ?? 0,
    ordinamento: Number(ordinamento) || 0,
    contenuti: [],
    sottoaree: [],
});

/**
 * Costruisce l'albero aree/sottoaree per la vista corsista.
 * Compatibile con i corsi gia' in corso: se le aree non hanno ordinamento
 * admin, l'ordine resta quello gia' visibile (derivato da contenuti.ordinamento).
 */
export const buildCourseContentAreaTree = (contenuti = [], areeCorso = []) => {
    const areeMap = new Map();

    areeCorso.forEach((area) => {
        areeMap.set(area.id_area, createAreaNode({
            id: area.id_area,
            titolo: area.titolo,
            id_area_padre: area.id_area_padre,
            ordinamento: area.ordinamento,
        }));
    });

    contenuti.forEach((contenuto) => {
        const areaId = contenuto.id_area;

        if (!areeMap.has(areaId)) {
            areeMap.set(areaId, createAreaNode({
                id: areaId,
                titolo: contenuto.titolo_area,
                id_area_padre: contenuto.id_area_padre,
                ordinamento: contenuto.ordinamento_area,
            }));
        }

        const areaPadreId = contenuto.id_area_padre;
        if (areaPadreId && areaPadreId !== 0 && !areeMap.has(areaPadreId)) {
            areeMap.set(areaPadreId, createAreaNode({
                id: areaPadreId,
                titolo: contenuto.titolo_padre,
                id_area_padre: 0,
                ordinamento: contenuto.ordinamento_area_padre,
            }));
        }
    });

    contenuti.forEach((contenuto) => {
        const area = areeMap.get(contenuto.id_area);
        if (area) {
            area.contenuti.push(contenuto);
        }
    });

    areeMap.forEach((area) => {
        area.contenuti.sort(sortContenutiByOrdinamento);
    });

    const areeRadice = [];
    areeMap.forEach((area) => {
        if (area.id_area_padre === 0) {
            areeRadice.push(area);
        } else {
            const padre = areeMap.get(area.id_area_padre);
            if (padre) {
                padre.sottoaree.push(area);
            }
        }
    });

    areeRadice.forEach(enrichAreaSortMeta);

    const sortSottoareeRec = (area) => {
        area.sottoaree.sort(sortAreasByOrdinamento);
        area.sottoaree.forEach(sortSottoareeRec);
    };

    areeRadice.sort(sortAreasByOrdinamento);
    areeRadice.forEach(sortSottoareeRec);

    return areeRadice;
};
